import { CheckIcon } from 'lucide-react';
import { T, useT, useTX } from '@transifex/react';
import { DialogClose, DialogContent, DialogDescription, DialogTitle } from '~/components/ui/dialog';
import { Button } from '~/components/ui/button';
import { XpBadge } from '~/components/ui/gamification/icons/xp-badge';
import { cn, type SupportedLocale } from '~/lib/utils';
import {
  getIsoWeekdayInTimeZone,
  getIsoWeekdayShortLabel,
  WEEK_ISO_ORDER,
} from '~/lib/gamification-utils';

export type GamificationDailyLoginStatusModalProps = {
  weeklyLoginDays: number;
  requiredLoginDays: number;
  bonusXp: number;
  weeklyLoginWeekdays: number[];
  goalMet: boolean;
};

export function GamificationDailyLoginStatusModal({
  weeklyLoginDays,
  requiredLoginDays,
  bonusXp,
  weeklyLoginWeekdays,
  goalMet,
}: GamificationDailyLoginStatusModalProps) {
  const t = useT();
  const tx = useTX();
  const locale = (tx.getCurrentLocale() || 'en') as SupportedLocale;
  const todayIso = getIsoWeekdayInTimeZone(new Date());
  const loggedSet = new Set(weeklyLoginWeekdays);
  const normalizedWeeklyLoginDays = Math.max(0, weeklyLoginDays);
  const remaining = Math.max(0, requiredLoginDays - normalizedWeeklyLoginDays);
  const displayProgressDays = Math.min(normalizedWeeklyLoginDays, requiredLoginDays);

  return (
    <DialogContent
      showCloseButton={false}
      className={cn(
        `
          max-h-[calc(100vh-2rem)] w-[calc(100%-2rem)] max-w-[460px] gap-0
          overflow-x-hidden overflow-y-auto rounded-2xl border-0 bg-background-0
          p-0 text-text-900 shadow-xs
        `
      )}
    >
      <div className="flex flex-col items-center gap-lg p-lg" data-slot="daily-login-modal-body">
        <div className="flex w-full flex-col items-center gap-md">
          <div className="relative size-25 shrink-0">
            <div
              className="
                absolute top-1/2 left-1/2 size-20 -translate-x-1/2
                -translate-y-1/2
              "
            >
              <img
                src="/assets/gamification/daily-login.svg"
                alt=""
                className="size-full object-contain"
                aria-hidden
              />
            </div>
          </div>

          <div className="flex w-full flex-col items-center gap-sm">
            <DialogTitle className="text-center text-h1">
              {goalMet ? (
                <T _str="You've completed this week's login challenge!" />
              ) : (
                <T
                  _str="{count, plural, one {You've logged in # day this week} other {You've logged in # days this week}}"
                  count={normalizedWeeklyLoginDays}
                />
              )}
            </DialogTitle>

            <DialogDescription asChild>
              <div>
                {goalMet ? (
                  <p className="text-center text-body-big">
                    <T _str="Come back next week to earn more XP!" />
                  </p>
                ) : remaining > 0 ? (
                  <p
                    className="
                      flex flex-wrap items-center justify-center gap-xs
                      text-center text-body-big
                    "
                  >
                    <T
                      _str="{remaining, plural, one {# more day to unlock {xpBadge} bonus} other {# more days to unlock {xpBadge} bonus}}"
                      remaining={remaining}
                      xpBadge={<XpBadge xp={bonusXp} />}
                    />
                  </p>
                ) : (
                  <span className="sr-only">
                    <T _str="Progress toward your weekly login bonus." />
                  </span>
                )}
              </div>
            </DialogDescription>
          </div>
        </div>

        <div
          className="
            flex w-full flex-col gap-sm rounded-md border border-line-400
            bg-background-0 px-lg py-md
          "
          data-slot="daily-login-week-card"
        >
          <div className="flex w-full items-center justify-between gap-sm">
            <p className="text-body-big-bold">
              <T _str="This Week" />
            </p>
            <p className="text-body-small">
              {displayProgressDays} / {requiredLoginDays} <T _str="days" />
            </p>
          </div>

          <div
            className="flex w-full items-center justify-between gap-xs"
            role="list"
            aria-label={t('Login days this week')}
          >
            {WEEK_ISO_ORDER.map(iso => {
              const label = getIsoWeekdayShortLabel(iso, locale);
              const isToday = iso === todayIso;
              const isLoggedIn = loggedSet.has(iso);
              return (
                <div
                  key={iso}
                  className="flex min-w-0 flex-1 flex-col items-center gap-xs"
                  role="listitem"
                >
                  <span
                    className={cn(
                      'text-center whitespace-nowrap',
                      isToday ? 'text-body-big-bold text-yellow-700' : 'text-body-big text-text-500'
                    )}
                  >
                    {label}
                  </span>
                  <div className="relative size-6 shrink-0">
                    {isLoggedIn ? (
                      <CheckIcon
                        className="
                          size-6 rounded-full bg-yellow-500 p-xs text-text-0
                        "
                      />
                    ) : (
                      <span className="block size-6 rounded-full bg-line-400" aria-hidden="true" />
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        <DialogClose asChild>
          <Button
            type="button"
            variant="default"
            className="h-auto min-h-0 min-w-24 shrink-0 px-md py-sm"
          >
            <T _str="Got It!" />
          </Button>
        </DialogClose>
      </div>
    </DialogContent>
  );
}
